import { useState } from 'react';
import { useDispatch, useStore } from 'react-redux';

import { WindxSimulacaoAntiga } from 'helper/windx';
import {
  LIMITE_SIMULACOES_ANTIGAS_ATIVAS,
  realizarConsulta,
  selectWindxFiltrosCompletos,
  toggleSimulacaoAntigaAtiva,
} from 'redux/reducers/windxControlPanelReducer';
import {
  useDeleteSimulacaoAntigaMutation,
  useGetSimulacoesAntigasQuery,
} from 'services/api/windx';

import useSimulacoesAntigasAtivas from './useSimulacoesAntigasAtivas';

export const LIMITE_POR_USUARIO = 10;

export type ModalState =
  | { tipo: 'fechado' }
  | { tipo: 'cadastrar' }
  | { tipo: 'editar'; sim: WindxSimulacaoAntiga };

/**
 * Estado e acoes da lista de simulacoes antigas (FE-WINDX-043 v7),
 * compartilhados pelo accordion desktop e pela secao do painel mobile.
 *
 * Extraido do container desktop sem mudanca de comportamento — inclusive o
 * `useStore()`: `useSelector` devolveria o valor PRE-dispatch (closure do
 * render), e o delete altera `idsSimulacoesAntigasAtivas`, que compoe
 * `filtrosCompletos` em SH. Por isso o store e lido APOS o dispatch para
 * decidir se auto-dispara a consulta.
 */
export default function useSimulacoesAntigasSalvas() {
  const dispatch = useDispatch();
  const store = useStore();
  const [modal, setModal] = useState<ModalState>({ tipo: 'fechado' });

  const { data: lista, isLoading } = useGetSimulacoesAntigasQuery();
  const [deleteSimulacao, { isLoading: isDeleting }] = useDeleteSimulacaoAntigaMutation();

  const simsAtivas = useSimulacoesAntigasAtivas();
  const idsAtivos = simsAtivas.map((s) => s.id);
  const atingiuLimiteAtivas = idsAtivos.length >= LIMITE_SIMULACOES_ANTIGAS_ATIVAS;

  const items = lista ?? [];
  const atingiuLimiteSalvas = items.length >= LIMITE_POR_USUARIO;

  const handleToggle = (sim: WindxSimulacaoAntiga) => {
    dispatch(toggleSimulacaoAntigaAtiva(sim.id));
  };

  const handleEditar = (sim: WindxSimulacaoAntiga) => {
    setModal({ tipo: 'editar', sim });
  };

  const handleDeletar = async (sim: WindxSimulacaoAntiga) => {
    // Se a simulacao deletada estava ativa, remove do array antes do delete
    // para nao deixar pointer dangling.
    if (idsAtivos.includes(sim.id)) {
      dispatch(toggleSimulacaoAntigaAtiva(sim.id));
      if (selectWindxFiltrosCompletos(store.getState())) {
        dispatch(realizarConsulta());
      }
    }
    await deleteSimulacao(sim.id);
  };

  const handleCloseModal = () => setModal({ tipo: 'fechado' });
  const abrirCadastro = () => setModal({ tipo: 'cadastrar' });

  return {
    items,
    isLoading,
    isDeleting,
    idsAtivos,
    atingiuLimiteAtivas,
    atingiuLimiteSalvas,
    modal,
    abrirCadastro,
    handleToggle,
    handleEditar,
    handleDeletar,
    handleCloseModal,
  };
}
